/* ==========================================================================
   Phone field with country code - shared by the promo popup, style-consult popup,
   contact page and store appointment forms. Pairs with assets/phone-field.css and
   needs assets/country-dial-codes.js loaded first (both `defer`, in that order).

   MARKUP - the section supplies its own classes for looks; behaviour hangs off the
   data attributes:

     <div class="pf" data-phone-field [data-pf-error="Please enter a valid phone number."]>
       <div class="pf__cc">
         <span class="pf__flag" data-pf-flag aria-hidden="true"></span>
         <span class="pf__dial" data-pf-dial aria-hidden="true">+91</span>
         <svg class="pf__chevron">…</svg>
         <select class="pf__select" data-pf-country data-fs-skip data-default="IN" aria-label="Country code">
           <option value="IN" data-dial="91" data-lengths="10" selected>India (+91)</option>
         </select>
       </div>
       <input type="tel" data-pf-number [required] …>          (NO name)
       <input type="hidden" name="phone" data-pf-value>         (optional)
     </div>

   The visible number has no name. The hidden data-pf-value input carries the form's
   original field name and always holds the E.164 number ("+919876543210"), so each
   form's existing submit code (form.elements['phone'] etc.) gets the full number
   without changes. Leave the hidden input out where the phone must NOT be posted
   (the promo popup's Shopify customer form) and read el.phoneField.value() instead.

   VALIDATION - the digit count after the calling code must be one of the country's
   national number lengths (libphonenumber data, a SET: NL is [9, 11]). Invalid
   numbers get setCustomValidity(), so checkValidity()/reportValidity() and
   field-state.js all see it with a message that names the expected length.
   Forgiving about how people type: spaces, dashes and brackets are dropped, a
   leading trunk "0" is dropped, and a pasted "+91…" / "0091…" / "91…" matching the
   selected country is removed.

   API: container.phoneField = { value(), country(), validate(), sync() }
        window.PhoneField.init(root)
   ========================================================================== */

(function () {
  'use strict';

  if (window.PhoneField) return; // several sections may each load this

  var DEFAULT_ERROR = 'Please enter a valid phone number.';

  function flag(iso) {
    if (!iso || iso.length !== 2) return '';
    return String.fromCodePoint(127397 + iso.charCodeAt(0), 127397 + iso.charCodeAt(1));
  }

  function selected(select) {
    return select.options[select.selectedIndex] || select.options[0];
  }

  function lengthsOf(opt) {
    return (opt && opt.getAttribute('data-lengths') || '').split(',').filter(Boolean).map(Number);
  }

  /* '' = empty, null = invalid, otherwise the E.164 string. */
  function parse(raw, dial, lengths) {
    raw = String(raw || '').trim();
    if (!raw) return '';
    if (/[^0-9+()\s.\-]/.test(raw)) return null; // letters etc. - reported, not stripped

    var digits = raw.replace(/\D/g, '');
    var intl = raw.charAt(0) === '+' || raw.indexOf('00') === 0;

    if (raw.indexOf('00') === 0) digits = digits.slice(2);
    if (digits.indexOf(dial) === 0 && (intl || lengths.indexOf(digits.length - dial.length) !== -1)) {
      digits = digits.slice(dial.length);
    } else if (intl) {
      return null; // international prefix for a different country than the one picked
    }
    digits = digits.replace(/^0+/, '');

    var ok = lengths.length
      ? lengths.indexOf(digits.length) !== -1
      : digits.length >= 4 && digits.length + dial.length <= 15; // not recorded: E.164 bounds
    return ok ? '+' + dial + digits : null;
  }

  function fillCountries(select) {
    var rows = window.COUNTRY_DIAL_CODES;
    if (!rows || select.getAttribute('data-filled')) return;
    var current = select.value || select.getAttribute('data-default') || 'IN';
    var frag = document.createDocumentFragment();
    for (var i = 0; i < rows.length; i++) {
      var o = document.createElement('option');
      o.value = rows[i][0];
      o.textContent = rows[i][1] + ' (+' + rows[i][2] + ')';
      o.setAttribute('data-dial', rows[i][2]);
      o.setAttribute('data-lengths', rows[i][3].join(','));
      if (rows[i][0] === current) { o.selected = true; o.defaultSelected = true; } // survives form.reset()
      frag.appendChild(o);
    }
    select.innerHTML = '';
    select.appendChild(frag);
    if (select.selectedIndex < 0) select.value = 'IN';
    select.setAttribute('data-filled', 'true');
  }

  function setup(root) {
    if (root.phoneField) return;

    var select = root.querySelector('[data-pf-country]');
    var number = root.querySelector('[data-pf-number]');
    if (!select || !number) return;
    var hidden = root.querySelector('[data-pf-value]');
    var flagEl = root.querySelector('[data-pf-flag]');
    var dialEl = root.querySelector('[data-pf-dial]');
    var errorText = root.getAttribute('data-pf-error') || DEFAULT_ERROR;

    fillCountries(select);
    if (!number.getAttribute('inputmode')) number.setAttribute('inputmode', 'tel');

    function state() {
      var opt = selected(select);
      var dial = opt ? opt.getAttribute('data-dial') : '91';
      var lengths = lengthsOf(opt);
      return { opt: opt, dial: dial, lengths: lengths, e164: parse(number.value, dial, lengths) };
    }

    function message(s) {
      return s.lengths.length
        ? errorText + ' (' + s.lengths.join(' or ') + ' digits after +' + s.dial + ')'
        : errorText;
    }

    /* Keeps the flag/dial label, the hidden E.164 value and the custom validity in
       step with what's typed. Emptiness is left to `required`. */
    function sync() {
      var s = state();
      if (flagEl) flagEl.textContent = s.opt ? flag(s.opt.value) : '';
      if (dialEl) dialEl.textContent = '+' + s.dial;
      number.setCustomValidity(s.e164 === null ? message(s) : '');
      if (hidden) hidden.value = s.e164 || '';
      return s;
    }

    /* Capture phase, so on the same keystroke this runs before field-state.js's own
       input listener and it evaluates the up-to-date validity. */
    number.addEventListener('input', sync, true);
    number.addEventListener('change', sync, true);

    select.addEventListener('change', function () {
      sync();
      // Let field-state.js (or anything else watching the number) re-check it.
      number.dispatchEvent(new Event('change', { bubbles: true }));
    });

    var form = number.form;
    if (form) {
      // Submit handlers read the hidden value, so make sure it's current first.
      form.addEventListener('submit', function () { sync(); }, true);
      form.addEventListener('reset', function () { setTimeout(sync, 0); });
    }

    root.phoneField = {
      value: function () { return sync().e164; },
      country: function () { var o = selected(select); return o ? o.value : ''; },
      validate: function () { var s = sync(); return s.e164 === '' ? !number.required : s.e164 !== null; },
      message: function () { return message(state()); },
      sync: sync
    };
    sync();
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-phone-field]'), setup);
  }

  window.PhoneField = { init: init, parse: parse };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
})();
